import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import Hero, { CurrentBackdrop, LiveBackdrop } from "@/components/Hero";

const VideoBackdrop = () => (
  <>
    <video className="heroA-video" src="/heroA.mp4" poster="/heroA-poster.jpg" autoPlay muted loop playsInline />
    <div className="hero-sheen" />
  </>
);

const BoardroomBackdrop = ({ srcs = ["/heroB-1.jpg", "/heroB-2.jpg", "/heroB-3.jpg"] }) => (
  <>
    {srcs.map((src, i) => (
      <div key={src} className={`heroB-slide heroB-slide-${i + 1}`}>
        <img src={src} alt="" />
      </div>
    ))}
    <span className="heroB-line" />
  </>
);

const options = [
  { id: "current", short: "Live now", label: "Live now · Video + Boardroom", note: "Your live site today", el: <LiveBackdrop /> },
  { id: "prev", short: "Previous", label: "Previous · Night office", note: "The old version", el: <CurrentBackdrop /> },
  { id: "a", short: "A · Video", label: "Option A · Living Video", note: "Same scene, the team actually moves", el: <VideoBackdrop /> },
  {
    id: "c",
    short: "C · New teammate",
    label: "Option C · Younger Eurasian teammate",
    note: "Sample only, not on your site",
    el: <BoardroomBackdrop srcs={["/sample-mixed-heroB-1.jpg", "/sample-mixed-heroB-2.jpg", "/sample-mixed-heroB-3.jpg"]} />,
  },
  { id: "b", short: "B · Boardroom", label: "Option B · Blue-Hour Boardroom", note: "New scene, gold-line wipe transitions", el: <BoardroomBackdrop /> },
];

const casts = [
  { src: "/heroA-poster.jpg", label: "Current preview cast", note: "Used in Options A & B" },
  { src: "/cast-1.jpg", label: "Cast 1", note: "Silver-haired lead, blonde exec, braids, young Latino" },
  { src: "/cast-2.jpg", label: "Cast 2", note: "Bearded exec, Korean lead, young white man, Middle Eastern" },
  { src: "/cast-3.jpg", label: "Cast 3", note: "Locs, Latina exec, East Asian, blond oxford" },
];

const CastGallery = () => (
  <section data-testid="cast-gallery" className="mt-14">
    <h2 className="font-serif text-white text-[26px] sm:text-[30px] leading-tight mb-1">Team options</h2>
    <p className="text-white/60 text-[14px] mb-6">Sample four-person teams in the same scene. Tell me which one you like and I&apos;ll use it.</p>
    <div className="grid sm:grid-cols-2 gap-4">
      {casts.map((c, i) => (
        <figure key={c.src} data-testid={`cast-${i}`} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]">
          <img src={c.src} alt={c.label} loading="lazy" className="w-full aspect-[1376/768] object-cover" />
          <figcaption className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-white">
            <span className="font-serif text-[18px]">{c.label}</span>
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/50">{c.note}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const MiniHero = ({ opt, zoom }) => (
  <figure data-testid={`compare-${opt.id}`} className="rounded-2xl overflow-hidden border border-white/10 bg-[var(--night-deep)]">
    <div className="relative overflow-hidden" style={{ zoom }}>
      <Hero backdrop={opt.el} />
    </div>
    <figcaption className="px-4 py-3 flex items-center justify-between text-white">
      <span className="font-serif text-[18px]">{opt.label}</span>
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">{opt.note}</span>
    </figcaption>
  </figure>
);

const useZoom = (cols) => {
  const calc = () => Math.min(1, (window.innerWidth - 48 - (cols - 1) * 16) / cols / 1440);
  const [z, setZ] = useState(calc);
  useEffect(() => {
    const on = () => setZ(calc());
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [cols]); // eslint-disable-line react-hooks/exhaustive-deps
  return z;
};

const Switcher = ({ mode, setMode }) => (
  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex flex-nowrap overflow-x-auto gap-1 rounded-full bg-[rgba(14,24,48,0.88)] backdrop-blur-xl border border-white/15 p-1.5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] max-w-[96vw]">
    {[...options.map((o) => [o.id, o.short]), ["compare", "Side by side"]].map(([id, label]) => (
      <button
        key={id}
        type="button"
        data-testid={`preview-switch-${id}`}
        onClick={() => setMode(id)}
        className={`shrink-0 whitespace-nowrap rounded-full px-3 sm:px-4 py-2 text-[12px] sm:text-[13px] font-medium transition-colors ${
          mode === id ? "bg-[#E8CE95] text-[var(--night)]" : "text-white/80 hover:text-white hover:bg-white/10"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

export default function HeroPreview() {
  const [mode, setMode] = useState(window.location.hash === "#c" ? "c" : "compare");
  const cols = window.innerWidth >= 1200 ? 2 : 1;
  const zoom = useZoom(cols);
  const single = options.find((o) => o.id === mode);

  return (
    <div data-testid="hero-preview-page" className="min-h-screen bg-[var(--night-deep)]">
      <div className="fixed top-0 inset-x-0 z-[70] bg-[#E8CE95] text-[var(--night)] text-center text-[12px] font-mono tracking-[0.18em] uppercase py-1.5">
        Preview only · your live site is unchanged
      </div>
      {single ? (
        <div key={single.id} className="pt-7">
          <Nav />
          <Hero backdrop={single.el} />
        </div>
      ) : (
        <div className="pt-16 pb-28 px-6">
          <h1 className="font-serif text-white text-[30px] sm:text-[38px] leading-tight mb-2">
            Hero animation comparison
          </h1>
          <p className="text-white/60 text-[14px] mb-8">
            All versions run live. Tap any option below to see it full-screen with the menu.
          </p>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {options.map((o) => (
              <MiniHero key={o.id} opt={o} zoom={zoom} />
            ))}
          </div>
          <CastGallery />
        </div>
      )}
      <Switcher mode={mode} setMode={setMode} />
    </div>
  );
}
